import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Chip,
  Divider,
  LinearProgress,
  ListItem,
  ListItemText,
  Stack,
  Typography
} from "@material-ui/core";
import { Add as AddIcon, HistoryEdu as HistoryEduIcon } from "@material-ui/icons";
import {
  displayDocumentStatus,
  listDocuments,
  type SignatureDocumentDisplayStatus
} from "services/signatures";
import { buildNewSignatureParams } from "components/_dashboard/signatures/GlobalNewSignatureDialog";
import { quickPickRoleForContact } from "components/_dashboard/signatures/NewSignatureForm";
import DocumentDetailPanel from "components/_dashboard/signatures/DocumentDetailPanel";
import QuestionnaireStepLabel from "components/_dashboard/signatures/QuestionnaireStepLabel";
import { signingSetOf } from "utils/signatureQuestionnaireStep";
import type { SignatureDocument } from "../../../../../@types/signatures";
import type { RealEstateContact } from "../../../../../@types/sharedSchema";

const statusColor = (status: SignatureDocumentDisplayStatus) => {
  switch (status) {
    case "completed":
      return "success" as const;
    case "declined":
    case "expired":
      return "error" as const;
    case "partial":
      return "warning" as const;
    case "sent":
      return "info" as const;
    default:
      return "default" as const;
  }
};

type Props = {
  contact: RealEstateContact;
};

export function ContactTableNestedRowSignatures({ contact }: Props): JSX.Element {
  const { t: translate } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // The document id IS the contact's phone (see `contactRekey.ts`). `callerPhoneNumber`
  // is a legacy mirror: absent on ~90% of documents and, where present, sometimes a
  // local-format string that agrees with nothing.
  const contactPhone = contact.id;

  const [documents, setDocuments] = useState<SignatureDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDocumentId, setOpenDocumentId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async (): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const result = await listDocuments({ contactId: contactPhone });
        if (cancelled) return;
        result.sort((a, b) => (b.sentAt || b.createdTime || "").localeCompare(a.sentAt || a.createdTime || ""));
        setDocuments(result);
      } catch (err: any) {
        if (cancelled) return;
        setError(err?.response?.data?.error || err?.message || "Failed to load signatures");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [contactPhone]);

  const openDocument = (id: string): void => {
    setOpenDocumentId(id);
  };

  const closeDocument = (): void => {
    setOpenDocumentId(null);
  };

  // Opens the globally-mounted NewSignatureForm dialog with this contact
  // prefilled — same pattern as ContactTableMenuRow's "Send document"
  // action. Uses contact.id (Firestore doc ID) not the phone number
  // because NewSignatureForm looks the contact up by doc ID.
  const openNewSignatureDialog = (): void => {
    const next = new URLSearchParams(searchParams);
    // The contact's customer type pre-selects the matching signing scenario
    // so its default template is picked automatically.
    const params = buildNewSignatureParams({
      contactId: contact.id,
      role: quickPickRoleForContact(contact) ?? undefined
    });
    Object.entries(params).forEach(([k, v]) => next.set(k, v));
    setSearchParams(next);
  };

  let body: JSX.Element;
  if (loading) {
    body = <LinearProgress />;
  } else if (error) {
    body = (
      <Typography variant="body2" color="error">
        {error}
      </Typography>
    );
  } else if (!documents.length) {
    body = (
      <Stack spacing={2} alignItems="center" sx={{ textAlign: "center", py: 6, px: 2 }}>
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            bgcolor: "primary.lighter",
            color: "primary.main",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <HistoryEduIcon sx={{ fontSize: 36 }} />
        </Box>
        <Stack spacing={0.5} alignItems="center">
          <Typography variant="h6">
            {translate("contactsPage.signatures.empty.title")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {translate("contactsPage.signatures.empty.subtitle")}
          </Typography>
        </Stack>
        <Button
          variant="contained"
          size="large"
          startIcon={<AddIcon />}
          onClick={openNewSignatureDialog}
        >
          {translate("signatures.newDocument")}
        </Button>
      </Stack>
    );
  } else {
    body = (
      <Stack divider={<Divider />} spacing={0}>
        {documents.map((doc) => {
          const sentAt = doc.sentAt ? new Date(doc.sentAt).toLocaleDateString("he-IL") : null;
          return (
            <ListItem
              key={doc.id}
              onClick={() => openDocument(doc.id)}
              sx={{
                cursor: "pointer",
                "&:hover": { bgcolor: "action.hover" }
              }}
            >
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="subtitle2" component="span">
                      {doc.formattedNumber}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" component="span">
                      · {doc.templateNameSnapshot}
                    </Typography>
                  </Stack>
                }
                secondary={
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: "wrap", rowGap: 0.5 }}>
                    <Chip
                      label={translate(`signatures.status.${displayDocumentStatus(doc)}`)}
                      color={statusColor(displayDocumentStatus(doc)) as any}
                      size="small"
                    />
                    {/* The order's questionnaire step, the sent list's own chip. */}
                    <QuestionnaireStepLabel doc={doc} />
                    {sentAt && (
                      <Typography variant="caption" color="text.secondary">
                        {sentAt}
                      </Typography>
                    )}
                  </Stack>
                }
                secondaryTypographyProps={{ component: "div" }}
              />
            </ListItem>
          );
        })}
      </Stack>
    );
  }

  return (
    <>
      {body}
      {openDocumentId && (
        <>
          <Box
            onClick={closeDocument}
            sx={{
              position: "fixed",
              inset: 0,
              bgcolor: "rgba(0,0,0,0.45)",
              zIndex: 2000
            }}
          />
          <Box
            role="dialog"
            aria-modal="true"
            sx={{
              position: "fixed",
              top: 0,
              bottom: 0,
              insetInlineEnd: 0,
              width: { xs: "100%", md: 480 },
              maxWidth: "100%",
              bgcolor: "background.paper",
              boxShadow: 6,
              zIndex: 2001,
              overflow: "auto",
              display: "flex",
              flexDirection: "column"
            }}
          >
            <DocumentDetailPanel
              documentId={openDocumentId}
              onClose={closeDocument}
              setDocuments={(() => {
                const open = documents.find((doc) => doc.id === openDocumentId);
                return open ? signingSetOf(open, documents) : undefined;
              })()}
            />
          </Box>
        </>
      )}
    </>
  );
}
